import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import StarRating from './StarRating';

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  padding: 1rem;
`;

const ModalContent = styled.div`
  background: var(--color-white);
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-xl);
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem 2rem;
  border-bottom: 1px solid var(--color-gray-200);
  text-align: center;
`;

const ModalTitle = styled.h2`
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-gray-900);
  margin-bottom: 0.5rem;
`;

const StoryTitle = styled.p`
  color: var(--color-gray-600);
  font-size: 0.9rem;
  margin: 0;
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const RatingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
`;

const RatingLabel = styled.label`
  font-weight: 600;
  color: var(--color-gray-700);
  font-size: 1rem;
`;


const ModalFooter = styled.div`
  padding: 1rem 2rem 2rem 2rem;
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  border-top: 1px solid var(--color-gray-200);
  
  @media (max-width: 480px) {
    flex-direction: column-reverse;
  }
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: var(--border-radius-md);
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid;
  
  ${({ variant = 'secondary' }) =>
    variant === 'primary'
      ? `
          background: var(--color-primary);
          color: var(--color-white);
          border-color: var(--color-primary);
          
          &:hover:not(:disabled) {
            background: var(--color-primary-dark);
            border-color: var(--color-primary-dark);
          }
        `
      : `
          background: var(--color-white);
          color: var(--color-gray-700);
          border-color: var(--color-gray-300);
          
          &:hover:not(:disabled) {
            background: var(--color-gray-50);
            border-color: var(--color-gray-400);
          }
        `}
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  background: var(--color-red-50);
  color: var(--color-red-700);
  padding: 0.75rem;
  border-radius: var(--border-radius-md);
  border: 1px solid var(--color-red-200);
  margin-bottom: 1rem;
  font-size: 0.9rem;
`;

interface RatingModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (rating: number) => Promise<void>;
  storyTitle: string;
  currentRating?: number;
  isSubmitting?: boolean;
  hasAlreadyRated?: boolean;
}

const RatingModal: React.FC<RatingModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  storyTitle,
  currentRating = 0,
  isSubmitting = false,
  hasAlreadyRated = false
}) => {
  const [rating, setRating] = useState(currentRating);
  const [error, setError] = useState('');
  
  useEffect(() => {
    if (isOpen) {
      setRating(currentRating);
      setError('');
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, currentRating]);
  
  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };
  
  const handleSubmit = async () => {
    // Don't allow submitting if no rating is selected and user hasn't already rated
    if (!hasAlreadyRated && rating === 0) {
      setError('Please select a rating before submitting.');
      return;
    }
    
    try {
      setError('');
      await onSubmit(rating);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to submit. Please try again.');
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      onClose();
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <ModalOverlay onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <ModalContent>
        <ModalHeader>
          <ModalTitle>{hasAlreadyRated ? 'Update Rating' : 'Rate Story'}</ModalTitle>
          <StoryTitle>"{storyTitle}"</StoryTitle>
        </ModalHeader>
        
        <ModalBody>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          
          <RatingSection>
            <RatingLabel>{hasAlreadyRated ? 'Update Your Rating' : 'Rate this Story'}</RatingLabel>
            <StarRating
              currentRating={rating}
              onRate={hasAlreadyRated ? () => {} : setRating}
              disabled={isSubmitting || hasAlreadyRated}
              showText={true}
              size="lg"
            />
            {hasAlreadyRated && (
              <p style={{ fontSize: '0.9rem', color: 'var(--color-gray-600)', margin: 0, textAlign: 'center' }}>
                You have already rated this story.
              </p>
            )}
          </RatingSection>
        </ModalBody>
        
        <ModalFooter>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting || (hasAlreadyRated ? true : rating === 0)}
          >
            {isSubmitting ? 'Submitting...' : (hasAlreadyRated ? 'Already Rated' : 'Submit Rating')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </ModalOverlay>
  );
};

export default RatingModal;